import { useFormContext, Controller } from 'react-hook-form';
import { FormControl, FormLabel, FormErrorMessage } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { AsyncSelect, Select, MultiValue } from 'chakra-react-select';
import { useMemo } from 'react';
import { useDebounceCallback } from 'usehooks-ts';

import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { availableStates } from '@duke-hq/svc-api/lib/states';
import { ProviderCauseVisibilitySchema } from '@duke-hq/svc-api-db/schemas/enums/ProviderCauseVisibility.schema';

export type SelectOption = {
  label: string;
  value: string;
};

type Visibility = keyof typeof ProviderCauseVisibilitySchema.enum;

interface CauseFormVisibilityFieldsProps {
  visibility: Visibility;
  onSelect: (selected: SelectOption[] | null) => void;
  disabled?: boolean;
}

interface RegionFieldsProps {
  defaultRegionOptions: SelectOption[];
  loadRegionOptions: (inputValue: string, callback: (options: SelectOption[]) => void) => void;
  isRegionsPending: boolean;
  onSelect: (selected: SelectOption[] | null) => void;
  disabled?: boolean;
}

interface SiteFieldsProps {
  defaultSiteOptions: SelectOption[];
  loadSiteOptions: (inputValue: string, callback: (options: SelectOption[]) => void) => void;
  isSitesPending: boolean;
  onSelect: (selected: SelectOption[] | null) => void;
  disabled?: boolean;
}

function RegionFields({
  defaultRegionOptions,
  loadRegionOptions,
  isRegionsPending,
  onSelect,
  disabled = false,
}: RegionFieldsProps): React.ReactElement {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <FormControl isRequired isInvalid={!!errors.siteRegionIds}>
      <FormLabel htmlFor="siteRegionIds">
        <Trans>Region</Trans>
      </FormLabel>
      <Controller
        name="siteRegionIds"
        control={control}
        render={({ field }) => {
          const selectedValues: string[] = Array.isArray(field.value)
            ? field.value.filter((v): v is string => typeof v === 'string')
            : [];
          const selectedOptions = defaultRegionOptions.filter((option) =>
            selectedValues.includes(option.value),
          );

          return (
            <AsyncSelect
              id="siteRegionIds"
              name="siteRegionIds"
              placeholder={t`Please select region`}
              defaultOptions={defaultRegionOptions}
              loadOptions={loadRegionOptions}
              selectedOptionColorScheme={'brand'}
              value={selectedOptions}
              onChange={(selectedOption: MultiValue<SelectOption>) => {
                const values: string[] = selectedOption.map((option: SelectOption) => option.value);
                field.onChange(values);
                onSelect([...selectedOption]);
              }}
              isLoading={isRegionsPending}
              isDisabled={disabled || defaultRegionOptions.length === 0}
              styles={{
                control: (provided: React.CSSProperties) => ({
                  ...provided,
                  backgroundColor: disabled ? '#f7fafc' : 'white',
                  color: disabled ? '#718096' : 'inherit',
                }),
              }}
              isMulti
            />
          );
        }}
      />
      <FormErrorMessage>
        {typeof errors.siteRegionIds?.message === 'string' ? errors.siteRegionIds.message : null}
      </FormErrorMessage>
    </FormControl>
  );
}

function SiteFields({
  defaultSiteOptions,
  loadSiteOptions,
  isSitesPending,
  onSelect,
  disabled = false,
}: SiteFieldsProps): React.ReactElement {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <FormControl isRequired isInvalid={!!errors.siteLocationIds}>
      <FormLabel htmlFor="siteLocationIds">
        <Trans>Site Location</Trans>
      </FormLabel>
      <Controller
        name="siteLocationIds"
        control={control}
        render={({ field }) => {
          const selectedValues: string[] = Array.isArray(field.value)
            ? field.value.filter((v): v is string => typeof v === 'string')
            : [];
          const selectedOptions = defaultSiteOptions.filter((option) =>
            selectedValues.includes(option.value),
          );

          return (
            <AsyncSelect
              id="siteLocationIds"
              name="siteLocationIds"
              placeholder={t`Please select site`}
              defaultOptions={defaultSiteOptions}
              loadOptions={loadSiteOptions}
              selectedOptionColorScheme={'brand'}
              value={selectedOptions}
              onChange={(selectedOption: MultiValue<SelectOption>) => {
                const values = selectedOption.map((option) => option.value);
                field.onChange(values);
                onSelect([...selectedOption]);
              }}
              isLoading={isSitesPending}
              isDisabled={disabled || defaultSiteOptions.length === 0}
              styles={{
                control: (provided: React.CSSProperties) => ({
                  ...provided,
                  backgroundColor: disabled ? '#f7fafc' : 'white',
                  color: disabled ? '#718096' : 'inherit',
                }),
              }}
              isMulti
            />
          );
        }}
      />
      <FormErrorMessage>
        {typeof errors.siteLocationIds?.message === 'string'
          ? errors.siteLocationIds.message
          : null}
      </FormErrorMessage>
    </FormControl>
  );
}

function StateFields({
  onSelect,
  disabled = false,
}: {
  onSelect: (selected: SelectOption[] | null) => void;
  disabled?: boolean;
}): React.ReactElement {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <FormControl isInvalid={!!errors.states} isRequired>
      <FormLabel htmlFor="SiteForm-state">
        <Trans>states</Trans>
      </FormLabel>
      <Controller
        name="states"
        control={control}
        render={({ field }) => {
          const selectedValues: string[] = Array.isArray(field.value)
            ? field.value.filter((v): v is string => typeof v === 'string')
            : [];
          const selectedOptions = availableStates.filter((option) =>
            selectedValues.includes(option.value),
          );

          return (
            <Select
              id="states"
              name="states"
              placeholder={t`Please select`}
              options={availableStates}
              inputId="SiteForm-states"
              value={selectedOptions}
              onChange={(selectedState: MultiValue<SelectOption>) => {
                const values = selectedState.map((option) => option.value);
                field.onChange(values);
                onSelect([...selectedState]);
              }}
              isDisabled={disabled}
              styles={{
                control: (provided: React.CSSProperties) => ({
                  ...provided,
                  backgroundColor: disabled ? '#f7fafc' : 'white',
                  color: disabled ? '#718096' : 'inherit',
                }),
              }}
              isMulti
            />
          );
        }}
      />
      <FormErrorMessage>
        {typeof errors.states?.message === 'string' ? errors.states.message : null}
      </FormErrorMessage>
    </FormControl>
  );
}

export function CauseFormVisibilityFields({
  visibility,
  onSelect,
  disabled = false,
}: CauseFormVisibilityFieldsProps): React.ReactElement | null {
  const { data: siteRegions, isPending: isRegionsPending } =
    trpcTenant.locations.listSiteRegions.useQuery(
      {},
      {
        enabled: Boolean(
          visibility != null &&
          (visibility === ProviderCauseVisibilitySchema.enum.REGION ||
            visibility === ProviderCauseVisibilitySchema.enum.STATE),
        ),
      },
    );

  const siteRegionsOptions = useMemo(() => {
    return (
      siteRegions?.map((region) => ({
        label: region.name,
        value: region.id,
      })) || []
    );
  }, [siteRegions]);

  const loadSiteRegionsOptions = useDebounceCallback<
    (
      inputValue: string,
      callback: (
        opts: {
          value: string;
          label: string;
        }[],
      ) => void,
    ) => void
  >((inputValue: string, callback) => {
    if (siteRegionsOptions.length > 0) {
      callback(
        siteRegionsOptions.filter(
          (option) =>
            option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
            option.value.toLowerCase().includes(inputValue.toLowerCase()),
        ),
      );
    } else {
      callback([]);
    }
  }, 500);

  const { data: siteLocations, isPending: isSitesPending } =
    trpcTenant.locations.listSiteLocations.useQuery(
      {
        filters: {
          available: true,
        },
      },
      {
        enabled: Boolean(
          visibility != null &&
          (visibility === ProviderCauseVisibilitySchema.enum.SITE ||
            visibility === ProviderCauseVisibilitySchema.enum.STATE),
        ),
      },
    );

  const sitesLocationsOptions = useMemo(() => {
    return (
      siteLocations?.map((site) => ({
        value: site.id,
        label: site.state ? `${site.name}, ${site.state}` : site.name,
      })) || []
    );
  }, [siteLocations]);

  const loadSitesLocationsOptions = useDebounceCallback<
    (
      inputValue: string,
      callback: (
        opts: {
          value: string;
          label: string;
        }[],
      ) => void,
    ) => void
  >((inputValue: string, callback) => {
    if (sitesLocationsOptions.length > 0) {
      callback(
        sitesLocationsOptions.filter(
          (option) =>
            option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
            option.value.toLowerCase().includes(inputValue.toLowerCase()),
        ),
      );
    } else {
      callback([]);
    }
  }, 500);

  switch (visibility) {
    case ProviderCauseVisibilitySchema.enum.REGION:
      return (
        <RegionFields
          defaultRegionOptions={siteRegionsOptions}
          loadRegionOptions={loadSiteRegionsOptions}
          isRegionsPending={isRegionsPending}
          onSelect={onSelect}
          disabled={disabled}
        />
      );
    case ProviderCauseVisibilitySchema.enum.SITE:
      return (
        <SiteFields
          defaultSiteOptions={sitesLocationsOptions}
          loadSiteOptions={loadSitesLocationsOptions}
          isSitesPending={isSitesPending}
          onSelect={onSelect}
          disabled={disabled}
        />
      );
    case ProviderCauseVisibilitySchema.enum.STATE:
      return <StateFields onSelect={onSelect} disabled={disabled} />;
    case ProviderCauseVisibilitySchema.enum.NATIONAL:
      return null;
    default:
      return null;
  }
}
